import * as d3 from 'd3';
import { throttle, isTouchDevice } from '../../utilities';
import axios from 'axios';

class Map {
  constructor(el, eventBus) {
    // console.log('Map');
    this.container = el;
    this.body = document.body;
    this.mapLocations = [];
    this.width = this.container.offsetWidth;
    this.height = this.container.offsetHeight;
    this.eventBus = eventBus;
    this.mapLoaded = false;
    this.canHover = !isTouchDevice();

    this.settings = {
      transitionSpeed: 500,
      markerSpeed: 180
    }
    
    this.countriesGeo;
    this.centered;
    this.projection;
    this.currentLocation;

    this.svg = d3.select(this.container)
      .append('svg')
      .attr('class', 'svg')
      .attr('preserveAspectRatio', 'xMidYMid')
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xlink', 'http://www.w3.org/1999/xlink')
      .attr('version', '1.1');

    this.resize = this.resize.bind(this);

    this.selectLocation = this.selectLocation.bind(this);
    this.resetMap = this.resetMap.bind(this);
    this.mapClicked = this.mapClicked.bind(this);
  }

  init() {
    return new Promise((res, rej) => {
      // load geojson data and map location data
      axios.all([this.getGeoJson(), this.getLocations()])
      .then(axios.spread((countriesGeo, mapLocations) => {
        this.countriesGeo = countriesGeo.data;
        this.mapLocations = mapLocations.data.data;

        this.draw();
        this.bindEventListeners();
        this.mapLoaded = true;
        // this.playMapIntro();
        res('map loaded');
      }))
      .catch(err => {
        console.log('error fetching data', err);
      });
    });
  }

  getGeoJson() {
    return axios.get('/assets/data/geojson/uk_ire_man_clean.json');
  }

  getLocations() {
    return axios.get('/api/locations');
  } 

  bindEventListeners() {
    window.addEventListener('resize', throttle(this.resize), 1000);
  }

  draw() {
    // create a unit projection
    this.projection = d3.geoMercator()
      .scale(1)
      .translate([0,0]);

    // create a path generator
    this.path = d3.geoPath()
      .projection(this.projection);

    // compute bounds of a point of interest, then derive scale and translate
    // use countriesGeo to center map on england, wales and northern ireland
    let b = this.path.bounds(this.countriesGeo),
      s = .95 / Math.max((b[1][0] - b[0][0]) / this.width, (b[1][1] - b[0][1]) / this.height),
      t = [(this.width - s * (b[1][0] + b[0][0])) / 2, (this.height - s * (b[1][1] + b[0][1])) / 2];

    // update the projection to use computed scale and translate....
    this.projection
      .scale(s)
      .translate(t);

    // remove if group already exists
    this.g = d3.select('.svg g');    
    if (this.g) this.g.remove();

    // set svg dimensions
    this.svg.attr('width', this.width)
      .attr('height', this.height)
      .attr('viewBox', `0 0 ${this.width} ${this.height}`);

    // create group
    this.g = this.svg.append('g')
      .attr('class', 'uk');

    // draw the svg of both the geojson and bounding box
    this.g.selectAll('path')
      .data(this.countriesGeo.features)
      .enter()
      .append('path')
      .attr('d', this.path)
      .attr('class', (d) => {
        if (d.properties.ADMIN === 'Ireland' || d.properties.ADMIN === 'Isle of Man') {
          return `country country--disabled`;
        }

        return `country`;
      });

    // remove all markers
    this.markers = d3.selectAll('.marker');
    if (this.markers) this.markers.remove();

    const activeLocations = this.mapLocations.filter(loc => loc.active);
    const otherLocations = this.mapLocations.filter(loc => !loc.active);

    this.g.selectAll('.markera')
      .data(activeLocations)
      .enter()
      .append('a')
      .attr('href', d => d.url)
      .attr('class', 'internal-link')
      .attr('data-location-id', d => d.id)
      .append('circle')
      .attr('r', '12')
      .attr('class', (d) => {
        return `marker active ${this.mapLoaded ? 'play-intro' : ''}`;
      })
      .attr('transform', (d) => {
        const [long, lat] = this.projection([d.long, d.lat]);
        return `translate(${long}, ${lat})`;
      })
      .on('click', (d) => {
        if (this.body.classList.contains('is-show-tooltip')) {
          this.body.classList.remove('is-show-tooltip');
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'Select Active Location',
          'event_category': 'Map Marker',
          'event_label': 'Navigation',
          'value': d.title
        });
      })
      .on('mouseover', (d) => {
        if (this.canHover) {
          this.eventBus.publish('marker-over', {title: d.title, comingSoon: false, isNextLocation: false});
        }
      })
      .on('mouseout', (d) => {
        if (this.canHover) {
          this.eventBus.publish('marker-out', {title: d.title, comingSoon: false, isNextLocation: false});
        }
      });

    this.g.selectAll('.markerb')
      .data(otherLocations)
      .enter()
      .append('circle')
      .attr('r', '12')
      .attr('class', (d) => {
        return `marker ${this.mapLoaded ? 'play-intro' : ''}`;
      })
      .attr('transform', (d) => {
        const [long, lat] = this.projection([d.long, d.lat]);
        return `translate(${long}, ${lat})`;
      })
      .on('click', (d) => {
        if (this.body.classList.contains('is-show-tooltip')) {
          this.body.classList.remove('is-show-tooltip');
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'Select Non-active Location',
          'event_category': 'Map Marker',
          'event_label': 'Navigation',
          'value': d.title
        });
      })
      .on('mouseover', (d) => {
        if (this.canHover) {
          this.eventBus.publish('marker-over', {title: d.title, comingSoon: true, isNextLocation: d.isNextLocation});
        }
      })
      .on('mouseout', (d) => {
        if (this.canHover) {
          this.eventBus.publish('marker-out', {title: d.title, comingSoon: true, isNextLocation: d.isNextLocation});
        }
      });
  }

  selectLocation(id) {
    return new Promise((resolve, reject) => {
      const selected = this.mapLocations.filter((location) => {
        return location.id === parseInt(id);
      });

      if (this.currentLocation !== selected[0]) {
        this.focus(selected[0])
          .then((res) => {
            resolve(res);
          });
      } else {
        resolve('No Need to focus map');
      }
    });
  }

  mapClicked() {
    return new Promise((resolve, reject) => {
      if (this.body.classList.contains('is-map-active')) {
          this.resetMap()
          .then((res) => {
            resolve(res);
          });
      } else {
        resolve('no need to close map');
      }
    });
  }

  focus(d) {
    return new Promise((resolve, rej) => {
      let x, y, k, w;
      const [long, lat] = this.projection([d.long, d.lat]);
      x = long;
      y = lat;
      k = 5;
      w = this.width / 2;

      this.body.classList.add('is-map-active');
      this.currentLocation = d;

      this.g.transition()
        .duration(this.settings.transitionSpeed)
        .ease(d3.easeQuadOut)
        .attr("transform", `translate(${w}, ${this.height / 2})scale(${k})translate(${-x}, ${-y})`)
        .style("stroke-width", 1 / k + "px")
        .on('end', () => {
          resolve('map focused');
        });
    });
  }

  resetMap() {
    return new Promise((resolve, reject) => {
      let x, y, k, w;
      x = this.width / 2;
      y = this.height / 2;
      k = 1;
      w = this.width / 2;

      this.body.classList.remove('is-map-active');
      this.currentLocation = null;

      this.g.transition()
        .duration(this.settings.transitionSpeed)
        .ease(d3.easeQuadOut)
        .attr("transform", `translate(${w}, ${this.height / 2})scale(${k})translate(${-x}, ${-y})`)
        .style("stroke-width", 1 / k + "px")
        .on('end', () => {
          resolve('map closed');
        });
    });
  }

  displayMarkers() {
    return new Promise((res, rej) => {
      const markers = document.querySelectorAll('.marker');
      if (markers.length){
        [...markers].forEach((marker, index) => {
          setTimeout(() => {
            marker.classList.add('play-intro');

            if (markers.length === index + 1) {            
              res('markers loaded');
            }
          }, index * this.settings.markerSpeed);
        });
      }
    });
  }

  displayMap() {
    this.body.classList.add('is-map-loaded');
  }

  playMapIntro() {
    const markers = document.querySelectorAll('.marker');
    if (markers.length) {
      [...markers].forEach((marker, index) => {
        setTimeout(() => {
          marker.classList.add('play-intro');
        }, index * this.settings.markerSpeed);
      });
      console.log('markers loaded');
      
    }
  }

  resize() {
    // only resize on chanfge of width to avoid resize on scroll
    if(this.container.offsetWidth != this.width) {
      this.width = this.container.offsetWidth;
      this.height = this.container.offsetHeight;
      // redraw the svg
      this.draw();
    }
  }
}

export default Map;